import { FirebaseError } from "firebase/app";
import { getAuth, signInAnonymously, signOut } from "firebase/auth";
import { update } from "firebase/database";
import { createReference } from "../lib/firebase";
import { FirebaseStore } from "../lib/FirebaseStore";
import type { GroupedPresenceData } from "./PresenceStore";
import { PresenceStore } from "./PresenceStore";

const FIREBASE_KEY = "voters";

// TYPES
// -----

export const VoteOption = {
  PINK: "pink",
  GREEN: "green",
} as const;

export const voteOptions = Object.values(VoteOption);

export type VoteOption = (typeof VoteOption)[keyof typeof VoteOption];

export interface VoterData {
  id: string;
  vote: VoteOption | null;
}

export interface VotersById {
  [id: string]: VoterData;
}

// ALL VOTERS
// ----------

interface AllVotersOptions {
  eventPrefix: string;
}

export class AllVotersStore extends FirebaseStore<VotersById> {
  private _eventPrefix: string;
  private _presenceStore: PresenceStore;
  private _auth = getAuth();

  constructor({ eventPrefix }: AllVotersOptions) {
    super(createReference(eventPrefix, FIREBASE_KEY));
    this._eventPrefix = eventPrefix;
    this._presenceStore = new PresenceStore({ eventPrefix });
  }

  async signIn() {
    try {
      const { user } = await signInAnonymously(this._auth);
      const id = user.uid;
      const voterRef = createReference(this._eventPrefix, FIREBASE_KEY, id);

      await update(voterRef, { id });

      return {
        success: true,
        userId: id,
      } as const;
    } catch (error) {
      console.error("An unknown error occurred while signing in");
      console.error(error);

      return {
        success: false,
        code: error instanceof FirebaseError ? error.code : "unknown",
      } as const;
    }
  }

  async getPresence() {
    const users = (await this.get()) ?? {};
    const userIds = Object.keys(users);

    return await this._presenceStore.getPresence(userIds);
  }

  subscribeToPresence(callback: (presence: GroupedPresenceData) => void) {
    let userIds: string[] | null = null;
    let presence: GroupedPresenceData | null = null;

    let unsubscribeFromUserStore = () => {};
    let unsubscribeFromPresenceStore = () => {};

    function notify() {
      if (!userIds || !presence) return;

      callback({
        online: presence.online.filter(({ userId }) => userIds!.includes(userId)),
        offline: presence.offline.filter(({ userId }) => userIds!.includes(userId)),
      });
    }

    unsubscribeFromUserStore = this.subscribe((users) => {
      userIds = Object.keys(users ?? {});
      notify();
    });

    unsubscribeFromPresenceStore = this._presenceStore.subscribeToPresence((data) => {
      presence = data;
      notify();
    });

    return () => {
      unsubscribeFromUserStore();
      unsubscribeFromPresenceStore();
    };
  }
}

// SINGLE VOTER
// ------------

interface VoterStoreOptions {
  eventPrefix: string;
  voterId: string;
}

export class VoterStore extends FirebaseStore<VoterData> {
  private _presenceStore: PresenceStore;
  readonly id: string;

  constructor({ eventPrefix, voterId }: VoterStoreOptions) {
    super(createReference(eventPrefix, FIREBASE_KEY, voterId));
    this._presenceStore = new PresenceStore({ eventPrefix });
    this.id = voterId;
  }

  trackPresence() {
    this._presenceStore.trackPresence(this.id);
  }

  async signOut() {
    await signOut(getAuth());
  }
}
